import React, {FC, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {makeAnalyticsAutomationReportsUrl} from '../../../analytics/helpers/analyticsUrlHelpers';
import {WorkspaceGenericItem} from '../../../components/inboxes/workspaces/workspaceGenericItem';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {
  getAnalyticsAutomationReportType,
  getAnalyticsParameters,
} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {fetchVisibleMetricsForReport} from '../../../store/panels/analytics/report/analyticsPanelReportActions';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {maybeRenderFeaturePillAndTooltip} from '../../helpers/metricLabels';
import {AnalyticsAutomationReportsEnum} from '../../helpers/reportHelpers/reportTypes';

/*
 * Props.
 */

interface ReportNavigationAutomationReportsLinkProps {
  selectedAutomationReportType: AnalyticsAutomationReportsEnum;
  iconName: IconName;
  selectedIconName: IconName;
  isDisabled: boolean;
  isBeta?: boolean;
  isNew?: boolean;
}

/*
 * Styles.
 */

const StyledAutomationReportsLinkWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/*
 * Component.
 */

export const ReportNavigationAutomationReportsLink: FC<
  PropsWithChildren<ReportNavigationAutomationReportsLinkProps>
> = ({selectedAutomationReportType, iconName, selectedIconName, children, isDisabled, isBeta, isNew}) => {
  const currentAutomationReportType: AnalyticsAutomationReportsEnum | undefined = useAppSelector(
    getAnalyticsAutomationReportType,
  );
  const dispatch = useAppDispatch();

  const isSelectedReport = selectedAutomationReportType === currentAutomationReportType;
  const parameters = useAppSelector(getAnalyticsParameters);

  const onAutomationReportsTypeClick = async (
    clickedAutomationReportsType: AnalyticsAutomationReportsEnum,
  ) => {
    const path = makeAnalyticsAutomationReportsUrl(clickedAutomationReportsType, parameters);
    await dispatch(navigateToLinkAction(path));
    dispatch(fetchVisibleMetricsForReport());
  };

  return (
    <WorkspaceGenericItem
      onClick={() => !isSelectedReport && onAutomationReportsTypeClick(selectedAutomationReportType)}
      isSelected={!isDisabled && isSelectedReport}
      iconName={iconName}
      selectedIconName={selectedIconName}
      shouldAlphabetizeWorkspace={false}
      workspaceDragItem={undefined}
      isDisabled={isDisabled}
    >
      <StyledAutomationReportsLinkWrapperDiv>
        {children}
        {maybeRenderFeaturePillAndTooltip(isBeta, isNew)}
      </StyledAutomationReportsLinkWrapperDiv>
    </WorkspaceGenericItem>
  );
};
